/**
 * The pipeline hint MODEL (pure): given the designer's live pipeline state, the one next step to
 * name. Kept apart from the component so tests (and the palette) can compute hints without React.
 */
export interface PipelineHintProps {
  editable: boolean;
  dirty: boolean;
  serverValidated: boolean;
  errorCount: number;
  version: number;
  publishedVersion: number | null;
  /**
   * OPSFL-FLOWOPS-1 A1: when the pipeline reaches its terminal state (the live version is the one
   * on the canvas) the hint stops being a dead end — it offers "Run it now". Optional so the pure
   * text hint keeps working where no start affordance is mounted.
   */
  onRunNow?: () => void;
}

export interface PipelineHintModel {
  text: string;
  detail: string;
  /** The terminal state's actionable next step. */
  action?: 'run';
}


export function computeHint(p: PipelineHintProps): PipelineHintModel | null {
  if (!p.editable) return null;
  if (p.dirty) {
    return {
      text: 'Next: Save — your edits are only in this browser',
      detail:
        'Save stores your changes as the draft version on the server. Nothing you added ' +
        '(nodes, edges, config) exists outside this tab until you save.',
    };
  }
  if (p.errorCount > 0) {
    return {
      text: `Next: fix ${p.errorCount} error${p.errorCount === 1 ? '' : 's'} in Problems`,
      detail:
        'The compiler refused this draft. Open the Problems panel (bottom) — each finding is ' +
        'anchored to its node/edge. Publish stays gated until the compile is clean.',
    };
  }
  if (!p.serverValidated) {
    return {
      text: 'Next: Validate — compile-check before publishing',
      detail:
        'Validate runs the server compiler: node config against each capability family, edge ' +
        'and outcome completeness, obligation (REG-##) coverage. Errors block publish; ' +
        'warnings never do.',
    };
  }
  if (p.publishedVersion == null || p.publishedVersion < p.version) {
    return {
      text: `Next: Publish… — make v${p.version} the runnable version`,
      detail:
        'Publish signs the flow (Ed25519) and freezes it — runs execute only signed, published ' +
        'versions. Diff shows the change class first: cosmetic/parameter changes (C0–C1) ' +
        'publish directly; structural, AI-node or obligation changes (C2–C4) require a second ' +
        'approver (maker-checker) before they go live.',
    };
  }
  return {
    text: `Published v${p.publishedVersion} is live — next: run it`,
    detail:
      'This version is signed and runnable. Start a run now (Run… / "Run it now"), or wire a ' +
      'trigger — a clock type, signal type or API name — from the flow list’s Triggers ' +
      'action so it starts on its own. Any edit you make now becomes the next draft version; ' +
      'the live version keeps running unchanged until you publish again.',
    action: 'run',
  };
}
